import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as courseApi from '../apis/courses';
import * as videoApi from '../apis/videos';

/**
 * 과정 생성 응답 Location 헤더에서 courseId 추출
 */
const parseCreatedCourseIdFromHeaders = (courseRes) => {
    const headers = courseRes?.headers;
    const location =
        headers?.location ||
        headers?.Location ||
        headers?.get?.('location') ||
        headers?.get?.('Location') ||
        headers?.['location'] ||
        headers?.['Location'];

    if (!location) {
        console.warn('Location 헤더를 찾을 수 없습니다. headers:', headers);
        return null;
    }
    const match = location.match(/\/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * 미디버스 카테고리 생성 실패가 아닌 경우에만 백엔드 생성분 롤백 후 에러 전파
 */
const rethrowAfterRollbackUnlessMidibusCategoryFailed = async (error, createdCourseId) => {
    if (error.midibusCategoryFailed) {
        throw error;
    }
    if (createdCourseId) {
        await courseApi.rollbackFailedCourseCreation(createdCourseId);
    }
    throw error;
};

// 쿼리 키 상수화
export const COURSE_KEYS = {
    all: ['courses'],
    lists: () => [...COURSE_KEYS.all, 'list'],
    list: (params) => [...COURSE_KEYS.lists(), params],
    details: () => [...COURSE_KEYS.all, 'detail'],
    detail: (id) => [...COURSE_KEYS.details(), id],
};

/* ==========================================
    Queries (GET)
========================================== */

// 1. 교육과정 목록 조회
export const useCourseList = (params) => {
    return useQuery({
        queryKey: COURSE_KEYS.list(params),
        queryFn: () => courseApi.fetchCourseList(params),
    });
};

// 2. 교육과정 상세 조회
// queryKey는 항상 문자열 id로 통일 (useParams courseId는 string, mutation은 number를 쓸 수 있어 무효화 불일치 방지)
export const useCourseDetail = (courseId) => {
    const idKey = courseId != null && courseId !== '' ? String(courseId) : courseId;
    return useQuery({
        queryKey: COURSE_KEYS.detail(idKey),
        queryFn: () => courseApi.fetchCourseDetail(courseId),
        enabled: !!courseId,
    });
};

/* ==========================================
    Mutations (CUD)
========================================== */

// 1. 교육과정 추가
/**
 * 교육과정 추가 시 미디버스 미디어 카테고리도 함께 생성합니다.
 * 프로세스:
 * 1. 백엔드에 과정 등록
 * 2. Midibus 인증 토큰 발급
 * 3. Midibus 채널 조회 (SECOLAB 채널 사용)
 * 4. Midibus 미디어 카테고리 생성 (과정명 기반)
 * 5. 미디버스 카테고리 생성 실패 시 백엔드 데이터 롤백
 */
export const useCreateCourse = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (courseData) => {
            let createdCourseId = null;
            try {
                // 1. 백엔드에 과정 등록
                const courseRes = await courseApi.createCourse({
                    ...courseData,
                });
                createdCourseId = parseCreatedCourseIdFromHeaders(courseRes);

                // 2. 미디버스 토큰 발급
                const tokenRes = await videoApi.fetchMidibusToken();
                const token = tokenRes.data?.data?.token;

                if (!token) {
                    throw new Error('미디버스 토큰 발급 실패');
                }
                // 3. 미디버스 채널 조회 (SECOLAB 채널 사용)
                const channelRes = await videoApi.fetchMidibusChannel(token);
                const channels = channelRes.data?.data || channelRes.data || [];
                const channelId = channels.channel_list?.find(channel => channel.channel_name === 'SECOLAB')?.channel_id;
                if (!channelId) {
                    throw new Error('SECOLAB 채널을 찾을 수 없습니다.');
                }
                // 4. 미디버스 카테고리 생성 (과정명 기반)
                const categoryRes = await videoApi.createMidibusCategory({
                    token,
                    name: createdCourseId,
                    channelId: channelId,
                });
                if (courseRes.status === 201 && categoryRes.status === 201) {
                    return { success: true, courseId: createdCourseId };
                }
                // 미디버스 카테고리 생성 실패 시 롤백하지 않고 에러 정보와 함께 반환
                const midibusError = new Error('미디버스 카테고리 생성 실패');
                midibusError.midibusCategoryFailed = true;
                midibusError.createdCourseId = createdCourseId;
                throw midibusError;
            } catch (error) {
                await rethrowAfterRollbackUnlessMidibusCategoryFailed(error, createdCourseId);
            }
        },
        onSuccess: () => {
            // 목록 갱신
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.lists() });
        },
    });
};

// 2. 교육과정 변경
export const useUpdateCourse = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: courseApi.updateCourse,
        onSuccess: (data, variables) => {
            // 목록 갱신
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.lists() });
            // 상세 정보 갱신 (수정 중인 데이터 반영)
            // variables.courseId가 FormData 안에 있어서 직접 접근이 어려울 수 있으니
            // React Component에서 mutate 호출 시 courseId를 별도 인자로 넘기거나,
            // FormData 객체 생성 전의 원본 객체를 참조해야 함.
            // 여기서는 목록 갱신을 우선으로 함.
            if (variables instanceof FormData) {
                // FormData인 경우 id 추출이 까다로우므로 전체 무효화가 안전함
                queryClient.invalidateQueries({ queryKey: COURSE_KEYS.details() });
            } else {
                queryClient.invalidateQueries({ queryKey: COURSE_KEYS.detail(String(variables.courseId)) });
            }
        },
    });
};

// 3. 교육과정 폐강 (soft delete)
/**
 * 교육과정을 폐강한다. 되돌릴 수 있는 처리이므로 미디버스 미디어는 삭제하지 않는다.
 * (미디버스까지 지우면 복구해도 영상 재생이 깨진 껍데기만 남는다.)
 * 진행 중인 그룹에 편성된 과정이면 409가 던져지며, force=true로 재요청해야 폐강된다.
 */
export const useDeleteCourse = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ courseId, force = false }) => courseApi.deleteCourse(courseId, force),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.detail(String(variables.courseId)) });
        },
    });
};

// 3-1. 교육과정 폐강 취소 (복구)
export const useRestoreCourse = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ courseId }) => courseApi.restoreCourse(courseId),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.detail(String(variables.courseId)) });
        },
    });
};

// 4. 교육과정 상태 변경 (hasContent 토글)
export const useUpdateCourseStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ courseId, hasContent }) => courseApi.updateCourseStatus(courseId, hasContent),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: COURSE_KEYS.detail(String(variables.courseId)) });
        },
    });
};